import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PrivateResearchItemDetailsData {
  id: string;
  user_id: string;
  workbook_id: string | null;
  page_id: string | null;
  title: string;
  content: string;
  original_prompt: string;
  research_type: any;
  research_parameters: any | null;
  sources: any[] | null;
  related_questions: any[] | null;
  metadata: any | null;
  system_prompt: string | null;
  usage: any | null;
  generated_research_count: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Hook for fetching detailed information about a single private research item
 * Mirrors the pattern used by useResearchItemDetails for public research
 * Uses a dedicated cache key for efficient invalidation
 */
export const usePrivateResearchItemDetails = (id: string | null) => {
  return useQuery({
    queryKey: ['private-research-item-details', id],
    queryFn: async (): Promise<PrivateResearchItemDetailsData> => {
      if (!id) throw new Error('No private research item ID provided');
      
      console.log('[usePrivateResearchItemDetails] Fetching details for ID:', id);
      
      // Get current user for RLS
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('User not authenticated');

      // Fetch research item from workbook_research table
      const { data: research, error: researchError } = await supabase
        .from('workbook_research')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id) // Security: ensure user owns this research
        // Allow access to ALL user research (both standalone and project-attached)
        .is('deleted_at', null) // Only active research
        .single();

      if (researchError) {
        console.error('[usePrivateResearchItemDetails] Error fetching research:', researchError);
        throw researchError;
      }
      
      if (!research) {
        throw new Error('Private research item not found');
      }

      console.log('[usePrivateResearchItemDetails] Successfully fetched research details');

      // Process sources - handle both string and array formats
      let sources = [];
      try {
        if (typeof research.sources === 'string') {
          sources = JSON.parse(research.sources);
        } else if (Array.isArray(research.sources)) {
          sources = research.sources;
        }
      } catch (e) {
        console.error('[usePrivateResearchItemDetails] Error parsing sources:', e);
        sources = [];
      }

      // Process related questions - handle both string and array formats
      let related_questions = [];
      try {
        if (typeof research.related_questions === 'string') {
          related_questions = JSON.parse(research.related_questions);
        } else if (Array.isArray(research.related_questions)) {
          related_questions = research.related_questions;
        }
      } catch (e) {
        console.error('[usePrivateResearchItemDetails] Error parsing related_questions:', e);
        related_questions = [];
      }

      return {
        id: research.id,
        user_id: research.user_id,
        workbook_id: research.workbook_id,
        page_id: research.page_id,
        title: research.title,
        content: research.content,
        original_prompt: research.original_prompt,
        research_type: research.research_type,
        research_parameters: research.research_parameters,
        sources,
        related_questions,
        metadata: research.metadata,
        system_prompt: research.system_prompt,
        usage: research.usage,
        generated_research_count: research.generated_research_count || 0,
        created_at: research.created_at,
        updated_at: research.updated_at,
        deleted_at: research.deleted_at
      };
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes - research details don't change often
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
  });
}; 